/* ============================================================
   GH-1133 S5 — FreePreviewContext (design §3.9 item 3): the context and its
   light hooks only. The provider lives in
   components/freePreview/FreePreviewProvider.jsx (it pulls in the wall,
   the API client and notifications), so screens that merely ASK "am I in
   the free preview?" keep a small import graph.

   The default value is "inactive": outside RequireActiveBilling's 'preview'
   decision every consumer (UpgradeContext, TASK-143 PreviewProvider,
   Dashboard, intercepts) behaves exactly as today.
   ============================================================ */
import React, { createContext, useCallback, useContext } from 'react';
import { freePublishingState } from '../lib/freePublishing.js';

const noop = () => {};

export const INACTIVE_FREE_PREVIEW = Object.freeze({
  active: false,
  readOnly: false,
  pendingVerification: false,
  mode: null,
  drafts: { ready: 0, limit: 3 },
  imagesLimit: 1,
  trialDays: 7,
  entitlements: null,
  wall: null,
  openTrialWall: noop,
  closeTrialWall: noop,
  refresh: async () => null,
});

export const FreePreviewContext = createContext(INACTIVE_FREE_PREVIEW);

export function useFreePreview() {
  return useContext(FreePreviewContext);
}

/** Renders `children` outside the preview and `preview` (default nothing) inside it. */
export function NotInFreePreview({ children, preview = null }) {
  const { active } = useFreePreview();
  return active ? preview : children;
}

/**
 * Wrap a user action: inside the preview it opens the trial wall (and sends
 * no request); outside it calls through unchanged.
 */
export function usePreviewGuard() {
  const freePreview = useFreePreview();
  return useCallback((source, action, meta) => (...args) => {
    if (freePublishingState(freePreview.entitlements) && ['edit', 'publish', 'schedule'].includes(source)) {
      return action?.(...args);
    }
    if (freePreview.active) {
      freePreview.openTrialWall(source, typeof meta === 'function' ? meta(...args) : meta);
      return undefined;
    }
    return action?.(...args);
  }, [freePreview]);
}
